import { CommandModel, CommandClickEventArgs, GridModule, SaveEventArgs } from '@syncfusion/ej2-angular-grids';
import { FieldAssignmentGridEditFormType } from './field-assignment-grid-edit-form-type';
import { IImportConfigurationFieldAssignmentExtendedDTO } from './../../configuration-field-assignment-extended-dto';
import { ImportFunction, ImportFileFormat } from './../../../../../../../base/models/models.swagger.generated';
import { ImportConfigurationEditFormsCoordinatorService } from '../../../import-configuration-edit-forms-coordinator.service';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ClientService } from 'src/app/base/services/client/client.service';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { Component, OnInit, Input } from '@angular/core';
import { FieldAssignmentGridEditFormComponent } from './field-assignment-grid-edit-form/field-assignment-grid-edit-form.component';
import { NgClass } from '@angular/common';
import { QueryBuilderModule } from '@syncfusion/ej2-angular-querybuilder';
import { startWith, takeUntil } from 'rxjs';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { FieldAssignmentDefinitionsService } from '../../../../field-assignment-definitions.service';

@Component({
    selector: 'app-field-assignment-grid',
    templateUrl: './field-assignment-grid.component.html',
    styleUrls: ['./field-assignment-grid.component.scss'],
    imports: [NgClass, GridModule, QueryBuilderModule, FieldAssignmentGridEditFormComponent, TranslateModule, ToolbarModule]
})
export class FieldAssignmentGridComponent extends GridBaseComponent<FieldAssignmentGridEditFormType> implements OnInit {

  @Input() public isColumnBased: boolean;

  public get fieldAssignments(): IImportConfigurationFieldAssignmentExtendedDTO[] {
    return this.formsCoordinationService.configurationFormFile.controls.fieldAssignments.value;
  }

  public ImportFunction = ImportFunction;

  constructor(
    translateService: TranslateService,
    public clientService: ClientService,
    public fieldAssignmentDefinitionsService: FieldAssignmentDefinitionsService,
    public formsCoordinationService: ImportConfigurationEditFormsCoordinatorService,
    private fb: FormBuilder) {
    super(translateService, null);
    this.gridCommands = [
      {
        type: 'None',
        title: this.translateService.instant('i18n.domain.import-configuration.edit.file.shared.set-value-to-no-data'),
        buttonOption:
        {
          cssClass: 'icon-btn',
          iconCss: 'e-icons icon-restart e-danger'
        }
      } as CommandModel,
      {
        type: 'Edit',
        title: this.translateService.instant('i18n.domain.import-configuration.edit.file.shared.view'),
        buttonOption:
        {
          cssClass: 'icon-btn',
          iconCss: 'icon-edit e-icons'
        }
      } as CommandModel, {
        type: 'None',
        title: this.translateService.instant('i18n.domain.import-configuration.edit.file.shared.reset'),
        buttonOption:
        {
          cssClass: 'icon-btn',
          iconCss: 'icon-reset e-icons e-danger'
        }
      } as CommandModel
    ];
  }

  public ngOnInit() {
    this.formsCoordinationService.configurationFormFile.controls.fileFormat.valueChanges.subscribe(() => {
      this.grid.refresh();
    });
    this.formsCoordinationService.configurationFormGeneral.controls.importType.valueChanges.subscribe(() => {
      setTimeout(() => { this.grid.refresh(); }, 0);
    });

    this.editDialogTitle = 'i18n.domain.import-configuration.edit.file.shared.edit.title';
  }

  protected override registerGridEditFormStatusChanges(saveBtn: any) {
    this.gridEditForm.statusChanges
      .pipe(
        startWith(this.gridEditForm.status),
        takeUntil(this.unsubscribeSubject),
      )
      .subscribe(status => {
        saveBtn.disabled = (status != 'VALID') || (JSON.stringify(this.formInitialValue) === JSON.stringify(this.gridEditForm.getRawValue()));
      });
  }

  public override actionComplete(args) {
    this.formsCoordinationService.configurationFormFile.controls.fieldAssignments.updateValueAndValidity();
    super.actionComplete(args);
  }

  public override actionBegin(args: SaveEventArgs) {
    super.actionBegin(args);
    if (args.requestType === 'beginEdit' || args.requestType === 'add') {
      // we need to override formInitialValue because we need to compare with getRawValue since sourceElmentPath is disabled
      this.formInitialValue = this.gridEditForm.getRawValue();
    }
  }

  public commandClick(args: CommandClickEventArgs) {
    if (args.commandColumn?.type === 'None') {
      if (args.commandColumn?.title === this.translateService.instant('i18n.domain.import-configuration.edit.file.shared.reset')) {
        this.resetFieldAssignment(args.rowData as IImportConfigurationFieldAssignmentExtendedDTO);
      } else if (args.commandColumn?.title === this.translateService.instant('i18n.domain.import-configuration.edit.file.shared.set-value-to-no-data')) {
        this.setNoDataForNonMandatoryFields(args.rowData as IImportConfigurationFieldAssignmentExtendedDTO);
      }
    }
  }

  public onRowDataBound(args: any) {
    if (this.isFieldMandatory(args.data) || args.data.importFunction === ImportFunction.NoData) {
      var button = args.row.cells[args.row.cells.length - 1].getElementsByClassName('icon-restart');
      if (button && button.length > 0 && button[0].parentElement) {
        button[0].parentElement.hidden = true;
      }
    }
  }

  protected initializeEditForm(data: IImportConfigurationFieldAssignmentExtendedDTO): void {
    this.gridEditForm = this.fb.group<FieldAssignmentGridEditFormType>({
      targetName: this.fb.control<string>({ value: data.targetName, disabled: true }, [Validators.required]),
      isGroupingElement: this.fb.control<boolean>(data.isGroupingElement),
      importFunction: this.fb.control<ImportFunction>(data.importFunction, [Validators.required]),
      fixValue: this.fb.control<string>(data.fixValue),
      cap: this.fb.control<number>(data.cap),
      sourceElementName: this.fb.control<string>({ value: data.sourceElementName, disabled: !this.isColumnBased }, [Validators.required]),
      sourceElementSelectionPath: this.fb.control<string>({ value: data.sourceElementSelectionPath, disabled: true }),
      position: this.fb.control<number>(data.position),
      setValueAsNull: this.fb.control<boolean>(/*data.setValueAsNull*/false),
      functionParameter1Value: this.fb.control<string>(data.functionParameter1Value),
      functionParameter2Value: this.fb.control<string>(data.functionParameter2Value),
      functionParameter3Value: this.fb.control<string>(data.functionParameter3Value),
      functionParameter4Value: this.fb.control<string>(data.functionParameter4Value),
      functionParameter5Value: this.fb.control<string>(data.functionParameter5Value)
    });
  }

  protected override getDataToSave(): IImportConfigurationFieldAssignmentExtendedDTO {
    const formValues = (this.gridEditForm as FormGroup<FieldAssignmentGridEditFormType>).getRawValue();
    return {
      targetName: formValues.targetName,
      isGroupingElement: formValues.isGroupingElement,
      importFunction: formValues.importFunction,
      fixValue: formValues.fixValue,
      cap: formValues.cap,
      sourceElementName: formValues.sourceElementName,
      sourceElementSelectionPath: formValues.sourceElementSelectionPath,
      position: formValues.position,
      functionParameter1Value: formValues.functionParameter1Value,
      functionParameter2Value: formValues.functionParameter2Value,
      functionParameter3Value: formValues.functionParameter3Value,
      functionParameter4Value: formValues.functionParameter4Value,
      functionParameter5Value: formValues.functionParameter5Value,
      valid: true
    } as IImportConfigurationFieldAssignmentExtendedDTO;
  }

  protected getEditDialogTitleParams(rowData: any) {
    return null;
  }

  private resetFieldAssignment(fieldassignment: IImportConfigurationFieldAssignmentExtendedDTO): void {
    const importType = this.formsCoordinationService.configurationFormGeneral.controls.importType.value;
    const targetDefinitions = this.fieldAssignmentDefinitionsService.getTargetDefinitionsImmediately(importType);

    //Cleanout all values
    this.resetFieldAssignmentDto(fieldassignment);

    //When its an edifact file, complete fieldAssignments with definitions
    if (this.formsCoordinationService.configurationFormFile.controls.fileFormat.value == ImportFileFormat.EDIFACT) {
      var targetDefinition = targetDefinitions.find(td => td.targetName === fieldassignment.targetName);
      fieldassignment.sourceElementName = targetDefinition.edifactSourceDefinition.sourceElementName;
      fieldassignment.sourceElementSelectionPath = targetDefinition.edifactSourceDefinition.sourceElementSelectionPath;
      fieldassignment.position = targetDefinition.edifactSourceDefinition.position;
    }

    //Select first supportedFunction
    fieldassignment.importFunction = targetDefinitions.find(td => td.targetName === fieldassignment.targetName).supportedFunctions[0];

    this.formsCoordinationService.validateFieldAssignments(importType);
    this.grid.refresh();
  }

  private resetFieldAssignmentDto(fieldassignment): IImportConfigurationFieldAssignmentExtendedDTO {
    fieldassignment.importFunction = null;
    fieldassignment.fixValue = null;
    fieldassignment.cap = null;
    fieldassignment.setValueAsNull = false;
    fieldassignment.sourceElementName = null;
    fieldassignment.sourceElementSelectionPath = null;
    fieldassignment.position = null;
    fieldassignment.functionParameter1Value = null;
    fieldassignment.functionParameter2Value = null;
    fieldassignment.functionParameter3Value = null;
    fieldassignment.functionParameter4Value = null;
    fieldassignment.functionParameter5Value = null;
    return fieldassignment;
  }

  public setNoDataForNonMandatoryFields(specificFieldAssignement: IImportConfigurationFieldAssignmentExtendedDTO = null): void {
    const importType = this.formsCoordinationService.configurationFormGeneral.controls.importType.value;
    const targetDefinitions = this.fieldAssignmentDefinitionsService.getTargetDefinitionsImmediately(importType);
    // if specificFieldAssignement is set, only set NoData for this field, else set NoData for all fields
    if (specificFieldAssignement) {
      var fieldassignment = this.fieldAssignments.find(fa => fa.targetName === specificFieldAssignement.targetName);
      var targetDefinition = targetDefinitions.find(td => td.targetName === fieldassignment.targetName);
      // check if targetDefinition has function no data
      if (targetDefinition.supportedFunctions.includes(ImportFunction.NoData)) {
        this.setNoDataToFieldAssignment(fieldassignment);
      }
    } else {
      this.fieldAssignments.forEach(fieldassignment => {
        var targetDefinition = targetDefinitions.find(td => td.targetName === fieldassignment.targetName);
        // check if targetDefinition has function no data
        if (targetDefinition.supportedFunctions.includes(ImportFunction.NoData)) {
          this.setNoDataToFieldAssignment(fieldassignment);
        }
      });
    }
    this.formsCoordinationService.validateFieldAssignments(importType);
    this.grid.refresh();
  }


  private setNoDataToFieldAssignment(fieldassignment): void {
    fieldassignment.fixValue = null;
    fieldassignment.cap = null;
    fieldassignment.sourceElementName = null;
    fieldassignment.sourceElementSelectionPath = null;
    fieldassignment.position = null;
    fieldassignment.functionParameter1Value = null;
    fieldassignment.functionParameter2Value = null;
    fieldassignment.functionParameter3Value = null;
    fieldassignment.functionParameter4Value = null;
    fieldassignment.functionParameter5Value = null;
    fieldassignment.importFunction = ImportFunction.NoData;
  }

  public isFieldMandatory(fieldassignment: IImportConfigurationFieldAssignmentExtendedDTO): boolean {
    const importType = this.formsCoordinationService.configurationFormGeneral.controls.importType.value;
    const targetDefinitions = this.fieldAssignmentDefinitionsService.getTargetDefinitionsImmediately(importType);
    var targetDefinition = targetDefinitions.find(td => td.targetName === fieldassignment.targetName);
    return targetDefinition && !targetDefinition.supportedFunctions.includes(ImportFunction.NoData);
  }
}
